/**
 * Web copy del helper de `mobile/src/utils/speciesMatch.ts`.
 *
 * El picker de variedades en la web (adjacent-crops/create) permite crear
 * una variedad sobre la marcha. Si lo que el auditor teclea es en realidad
 * el nombre de una especie del catálogo ("Tomates", "Aguacates", "palta"),
 * detectamos el caso y guardamos la variedad como "Genérica de especie X"
 * enlazada al `species_id` — misma UX que el móvil, mismo formato de
 * nombre para que el listado quede consistente entre canales.
 */

export interface SpeciesLike {
  id: string | number;
  name: string;
}

const SYNONYMS: Record<string, string[]> = {
  aguacate: ["aguacates", "palta", "paltas"],
  tomate: ["tomates", "tomatera", "tomateras"],
  naranja: ["naranjas", "naranjo", "naranjos"],
  mandarina: ["mandarinas", "mandarino", "mandarinos"],
  limon: ["limones", "limonero", "limoneros"],
  pomelo: ["pomelos"],
  mango: ["mangos"],
  papaya: ["papayas"],
  pina: ["pinas", "ananá", "ananás"],
  platano: ["platanos", "banana", "bananas"],
  uva: ["uvas", "parra", "parras", "vid", "vides"],
  manzana: ["manzanas", "manzano", "manzanos"],
  pera: ["peras", "peral", "perales"],
  melocoton: ["melocotones", "melocotonero"],
  albaricoque: ["albaricoques"],
  ciruela: ["ciruelas", "ciruelo", "ciruelos"],
  cereza: ["cerezas", "cerezo", "cerezos"],
  higo: ["higos", "higuera", "higueras"],
  granada: ["granadas", "granado", "granados"],
  kiwi: ["kiwis"],
  chirimoya: ["chirimoyas"],
  fresa: ["fresas", "fresal", "fresales"],
  frambuesa: ["frambuesas"],
  arandano: ["arandanos"],
  mora: ["moras"],
  aceituna: ["aceitunas", "olivo", "olivos", "oliva", "olivas"],
  almendra: ["almendras", "almendro", "almendros"],
  nuez: ["nueces", "nogal", "nogales"],
  avellana: ["avellanas", "avellano"],
  pistacho: ["pistachos"],
  guayaba: ["guayabas"],
  maracuya: ["maracuyas", "maracuyás"],
};

function normalize(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function depluralize(normalized: string): string[] {
  const out: string[] = [];
  if (normalized.endsWith("es") && normalized.length > 3) {
    out.push(normalized.slice(0, -2));
  }
  if (normalized.endsWith("s") && normalized.length > 2) {
    out.push(normalized.slice(0, -1));
  }
  return out;
}

export function matchSpecies<T extends SpeciesLike>(
  input: string,
  species: T[],
): T | null {
  const target = normalize(input);
  if (!target) return null;

  const byNorm = new Map<string, T>();
  for (const s of species) {
    if (!s?.name) continue;
    byNorm.set(normalize(s.name), s);
  }

  const exact = byNorm.get(target);
  if (exact) return exact;

  for (const singular of depluralize(target)) {
    const hit = byNorm.get(singular);
    if (hit) return hit;
  }

  for (const [canonical, alts] of Object.entries(SYNONYMS)) {
    if (alts.includes(target)) {
      const hit = byNorm.get(canonical);
      if (hit) return hit;
    }
    for (const singular of depluralize(target)) {
      if (alts.includes(singular)) {
        const hit = byNorm.get(canonical);
        if (hit) return hit;
      }
    }
  }

  return null;
}

export function genericVarietyName(speciesName: string): string {
  return `Genérica de especie ${speciesName.trim()}`;
}
